// Sound effects for Clompy Clicker, made with the Web Audio API so there are no audio files to load.

// How loud the sounds are with the volume slider at the top
const MAX_VOLUME = 0.3;
// Where the slider starts, as a part of that
const DEFAULT_VOLUME = 0.7;
// Where the player's choices to mute the game, and how loud to have it, are remembered
const MUTED_KEY = 'clompyClickerMuted';
const VOLUME_KEY = 'clompyClickerVolume';

let context = null;

// Browsers only allow sound after a click, so the audio context is made the first time a sound is played
function audio() {
    let AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) {
        return null;
    }
    if (!context) {
        context = new AudioContext();
    }
    if (context.state === 'suspended') {
        context.resume();
    }
    return context;
}

// Whether the player has muted the game (remembered between visits)
export function isMuted() {
    try {
        return localStorage.getItem(MUTED_KEY) === 'yes';
    } catch (e) {
        return false;
    }
}

export function setMuted(muted) {
    try {
        if (muted) {
            localStorage.setItem(MUTED_KEY, 'yes');
        } else {
            localStorage.removeItem(MUTED_KEY);
        }
    } catch (e) { /* storage unavailable: the choice lasts until the page is left */ }
}

// How loud the player has set the game, from 0 (silent) to 1 (remembered between visits)
export function getVolume() {
    try {
        let stored = localStorage.getItem(VOLUME_KEY);
        let volume = stored === null ? DEFAULT_VOLUME : Number(stored);
        return volume >= 0 && volume <= 1 ? volume : DEFAULT_VOLUME;
    } catch (e) {
        return DEFAULT_VOLUME;
    }
}

let volume = getVolume();

export function setVolume(level) {
    volume = Math.max(0, Math.min(1, level));
    try {
        localStorage.setItem(VOLUME_KEY, String(volume));
    } catch (e) { /* storage unavailable: the choice lasts until the page is left */ }
}

// One note: starts sharply and rings out over `length` seconds
function ping(ctx, type, frequency, start, length, peak) {
    let osc = ctx.createOscillator();
    let gain = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(frequency, start);
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, peak * MAX_VOLUME * volume), start + 0.004);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + length);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(start);
    osc.stop(start + length + 0.03);
}

// A click on the mug: the clink of a tapped cup, a bright ring over a short knock. The pitch shifts a little each
// time so that fast clicking doesn't drone.
export function playMugClick() {
    let ctx = audio();
    if (!ctx || volume === 0) {
        return;
    }
    let now = ctx.currentTime;
    let pitch = 0.94 + Math.random() * 0.12;
    ping(ctx, 'sine', 1480 * pitch, now, 0.16, 0.9);
    ping(ctx, 'sine', 2960 * pitch, now, 0.08, 0.35);
    ping(ctx, 'triangle', 320 * pitch, now, 0.05, 0.6);
}

// A golden diamond appearing: a soft twinkle of three high notes, quieter than the sound of catching one, to turn
// the player's head without startling them. (Browsers stay silent until the page has been clicked, so one that
// appears before the player's first click makes no sound.)
export function playGoldenAppear() {
    let ctx = audio();
    if (!ctx || volume === 0) {
        return;
    }
    let now = ctx.currentTime;
    // G6, D7, G7
    [1568, 2349.3, 3136].forEach((frequency, i) => {
        ping(ctx, 'sine', frequency, now + i * 0.09, 0.45, 0.4);
    });
    ping(ctx, 'triangle', 3136, now + 0.27, 0.6, 0.15);
}

// Catching a golden diamond: a quick run of bright notes climbing a chord, with a shimmer ringing on over the top
export function playGoldenCatch() {
    let ctx = audio();
    if (!ctx || volume === 0) {
        return;
    }
    let now = ctx.currentTime;
    // C6, E6, G6, C7, E7
    [1046.5, 1318.5, 1568, 2093, 2637].forEach((frequency, i) => {
        ping(ctx, 'triangle', frequency, now + i * 0.06, 0.32, 0.7);
        ping(ctx, 'sine', frequency * 2, now + i * 0.06, 0.18, 0.2);
    });
    ping(ctx, 'sine', 3136, now + 0.3, 0.7, 0.3);
    ping(ctx, 'sine', 4186, now + 0.34, 0.6, 0.18);
}
